import "./EquityOrderStep.scss";
import template from "./EquityOrderStep.html";
import {CompanyInfo} from "../../../../../../types/Company";
import {EquityOrderType, EquityWizardData} from "../../models/EquityWizardData";
import {EquityPosition, PortfolioPositions, PortfolioType} from "../../../../../models/Portfolio";
import {IFormController, IOnDestroy, IOnInit, ITimeoutService} from "angular";
import {PriceStrategy} from "../../../../../../components/inputs/price/input/PriceStrategy";
import {PriceStrategyService} from "../../../../../../components/inputs/price/service/PriceStrategyService";
import {NgFormUtil} from "../../../../../../js/util/NgFormUtil";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {isNotNull} from "../../../../../../js/util/UtilFunctions";
import {ObjectUtil} from "../../../../../../js/util/ObjectUtil";
import {BrokerageEstimationService} from "../../../../../../brokerage/services/BrokerageEstimationService";
import {PlaceOrderRequest} from "../../../services/models/PlaceOrderRequest";
import {OrderLifetime, OrderSide} from "../../../../../../orders/models/OrderRecord";
import {EquityOrderWizardStore} from "../../store/EquityOrderWizardStore";
import {PortfolioService} from "../../../../../service/PortfolioService";
import {QuoteViewModel} from "../../../../../../quote/models/QuoteViewModel";
import {extractOrderRequest, isSameBrokerageRequest} from "../../util/EquityWizardOrderUtil";
import {CurrentUserService} from "../../../../../../services/user/CurrentUserService";
import {FeatureNames} from "../../../../../../types/UserConfig";
import {ComponentCallback} from "../../../../../../types/AngularJS";
import {PricingInstruction} from "../../../../../../orders/models/PricingInstruction";
import {Exchange} from "../../../../../../instruments/exchange/Exchange";

export const EquityOrderStep = "tfEquityOrderStep";

export function equityOrderStep() {
    return {
        template,
        bindings: {
            store: "<",
            onAccountBalanceChange: "&"
        },
        controller: EquityOrderStepController,
        controllerAs: "ctrl"
    };
}

export class EquityOrderStepController implements IOnInit, IOnDestroy {

    /** @input */
    public readonly store: EquityOrderWizardStore;

    /** @output */
    public readonly onAccountBalanceChange: ComponentCallback;

    /** @ui-binding */
    public set orderForm(form: IFormController | null) {
        if (form != null) {
            this.orderForm$.next(form);
            this.form = form;
        }
    }

    private readonly orderForm$ = new ReplaySubject<IFormController>(1);

    /* Need access to the form in both the template and the observables, so we reassign to a controller variable */
    public form: IFormController;

    public readonly stopLossEnabled$ = new ReplaySubject<boolean>(1);
    public readonly code$ = new ReplaySubject<string>(1);
    public readonly exchange$ = new ReplaySubject<string>(1);
    public readonly pricingInstruction$ = new BehaviorSubject<PricingInstruction>(PricingInstruction.LIMIT);
    public readonly hasAccountSelected$ = new BehaviorSubject(false);
    public readonly quantityOwned$ = new BehaviorSubject(0);
    public readonly estimatedBrokerage$ = new BehaviorSubject<number | null>(null);
    public readonly quote$ = new ReplaySubject<QuoteViewModel>(1);

    /* Stupid NgModel bindings */
    public lastQuote: number | null;
    public side: OrderSide;
    public lifetime: OrderLifetime;
    public isParentOrder: boolean | null = false;
    public quantity: number | null;
    public totalValue: number;
    public consideration: number;
    public orderType: EquityOrderType;
    public strategy: PriceStrategy;
    public isStopLoss = false;
    public stopLossLimit: number | null = null;
    public isOma: boolean = false;
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly priceStrategyService: PriceStrategyService,
                       private readonly portfolioService: PortfolioService,
                       private readonly $timeout: ITimeoutService,
                       private readonly brokerageEstimationService: BrokerageEstimationService,
                       private readonly currentUserService: CurrentUserService) {
    }

    public $onInit(): void {
        this.currentUserService.current.subscribe(currentUser => this.isOma = currentUser.isOma());
        this.watchStopLossEnabled();
        this.watchCode();
        this.watchExchange();
        this.watchSelectedAccount();
        this.watchQuantityOwnedAndAccountBalance();
        this.watchQuoteChange();
        this.watchBrokerageEstimate();
        this.watchCodeChange();
        this.watchOrderProperties();
        this.watchOrderChanges();
        this.watchPricingInstruction();
    }

    private watchStopLossEnabled() {
        const hasFeature$ = this.currentUserService
                                .current
                                .map((user) => user.hasFeature(FeatureNames.STOP_LOSS_ORDERS));

        const isValidAccount$ = this.store
                                    .current
                                    .map((data) => data.portfolio?.portfolioType)
                                    .map((type) => type === PortfolioType.BROKER || type === PortfolioType.SIMULATED);

        const isSell$ = this.store
                            .current
                            .map((order) => order.side)
                            .map((side) => side === OrderSide.LONG_SELL || side === OrderSide.SHORT_SELL);

        Observable.combineLatest(isSell$, hasFeature$, isValidAccount$)
                  .takeUntil(this.destroyed$)
                  .map(([isSell, featureEnabled, isValidAccount]) => isSell && featureEnabled && isValidAccount)
                  .distinctUntilChanged()
                  .subscribe((enabled) => this.stopLossEnabled$.next(enabled));
    }

    private watchPricingInstruction() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.pricingInstruction)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((pricingInstruction) => this.pricingInstruction$.next(pricingInstruction));
    }

    private watchCode() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.code)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((code) => this.code$.next(code));
    }

    private watchExchange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.exchange)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((exchange) => this.exchange$.next(exchange));
    }

    private watchSelectedAccount() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((c) => c.portfolio)
            .map(isNotNull)
            .subscribe((hasAccount) => this.hasAccountSelected$.next(hasAccount));
    }

    private watchQuantityOwnedAndAccountBalance() {
        const portfolioPositions$ = this.buildCurrentPortfolio();
        const equityPositions$ = portfolioPositions$.map(p => p.equityPositions);
        const code$ = this.store.current.map((d) => d.code).filter(isNotNull);
        const source$ = Observable.combineLatest(code$, equityPositions$);

        portfolioPositions$.takeUntil(this.destroyed$)
                           .subscribe((position) => this.onAccountBalanceChange({balance: position.currentCmaBalance + position.currentEquityLedger + position.currentEtoLedger }));

        source$.takeUntil(this.destroyed$)
               .map(([code, positions]) => this.findPositionQuantity(code, positions))
               .subscribe((quantity) => this.quantityOwned$.next(quantity));
    }

    private buildCurrentPortfolio(): Observable<PortfolioPositions> {
        return this.store
                   .current
                   .takeUntil(this.destroyed$)
                   .map((d) => d.portfolio)
                   .filter(isNotNull)
                   .map((account) => account.id)
                   .distinctUntilChanged()
                   .switchMap((id) => this.fetchPositions(id))
                   .map((p) => {
                       return p;
                   })
                   .publishReplay(1)
                   .refCount();
    }

    private fetchPositions(portfolioId: number): Observable<PortfolioPositions> {
        return Observable.of(portfolioId)
                         .flatMap((id) => this.portfolioService.portfolio(id))
                         .catch(() => Observable.empty<PortfolioPositions>());
    }

    private findPositionQuantity(code: string, positions: ReadonlyArray<EquityPosition>): number {
        const position = positions.find((p) => p.code === code);
        if (position == null) {
            return 0;
        } else {
            return position.quantity;
        }
    }

    private watchQuoteChange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.quote)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((quote) => {
                this.quote$.next(quote);
                this.lastQuote = quote.last;
            });
    }

    private watchBrokerageEstimate() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .filter((order) => order.quantity !== 0)
            .map((order) => extractOrderRequest(order))
            .filter(isNotNull)
            .filter((request) => request.order.quantity != null && request.order.quantity !== 0)
            .distinctUntilChanged(isSameBrokerageRequest())
            .auditTime(1000)
            .switchMap((request) => this.retrieveBrokerageQuote(request.portfolioId, request.order))
            .subscribe((estimate) => this.estimatedBrokerage$.next(estimate));
    }

    private retrieveBrokerageQuote(portfolioId: number, request: PlaceOrderRequest): Observable<number | null> {
        const estimationRequest = ObjectUtil.pick(request, "code", "buy", "quantity", "limit", "legs", "pricingInstruction", "exchange");
        const response = this.brokerageEstimationService.estimate(portfolioId, estimationRequest);
        return Observable.fromPromise(response)
                         .catch(() => Observable.of(null));
    }

    private watchCodeChange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.code)
            .distinctUntilChanged()
            .filter(isNotNull)
            .map((code) => this.priceStrategyService.forCode(code))
            .subscribe((strategy) => this.$timeout(() => this.strategy = strategy));
    }

    private watchOrderProperties() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .subscribe((order) => this.$timeout(() => {
                this.side = order.side;
                this.lifetime = order.lifetime;
                this.isParentOrder = order.isParentOrder;
                this.quantity = order.quantity;
                this.totalValue = order.totalValue;
                this.consideration = order.quantity * (order.limit ?? 0);
                this.orderType = order.type ?? EquityOrderType.QUANTITY;
                this.isStopLoss = order.isStopLoss;
                this.stopLossLimit = order.stopLossLimit ?? null;
            }));
    }

    private watchOrderChanges() {
        const source$ = Observable.combineLatest(this.store.current,
                                                 this.orderForm$,
                                                 this.quantityOwned$,
                                                 this.hasAccountSelected$);
        source$.takeUntil(this.destroyed$)
               .subscribe(([order, form, quantityOwned, hasAccount]) => this.updateFormValidity(order,
                                                                                                form,
                                                                                                quantityOwned,
                                                                                                hasAccount));
    }

    public companyChanged(company: CompanyInfo) {
        this.orderForm$
            .first()
            .subscribe((form) => NgFormUtil.setPristine(form));

        const security = ObjectUtil.pick(company, "code", "exchange");
        const strategy = this.priceStrategyService.forCode(security.code);
        if (company.exchange === Exchange.AXW || company.exchange === Exchange.CXA) {
            this.lifetimeChanged(OrderLifetime.END_OF_DAY);
        }
        this.store.updateSecurity(security, strategy);
        this.store.updateName(company.name);
    }

    public setLimitToMid() {
        this.store.updateLimitToMid();
    }

    public limitChanged(limit: number | null) {
        if (limit != null) {
            this.store.updateLimit(limit);
        }
    }

    public pricingInstructionChanged(pricingInstruction: PricingInstruction) {
        if (pricingInstruction != null) {
            this.pricingInstruction$.next(pricingInstruction);
            this.store.updatePricingInstruction(pricingInstruction);
            if (pricingInstruction === PricingInstruction.MARKET_TO_LIMIT) {
                this.limitChanged(this.lastQuote);
            }
        }
    }

    public typeChanged(type: EquityOrderType) {
        this.store.updateOrderType(type);
    }

    public unitsChanged(units: number) {
        this.store.updateQuantity(units);
    }

    public totalValueChanged(totalValue: number) {
        this.store.updateTotalValue(totalValue);
    }

    public sideChanged(side: OrderSide) {
        this.store.updateSide(side);
    }

    public lifetimeChanged(lifetime: OrderLifetime) {
        this.store.updateLifetime(lifetime);
    }

    public isParentOrderChanged(isParentOrder: boolean) {
        this.store.updateIsParentOrder(isParentOrder);
        if (isParentOrder) {
            this.store.updateOrderType(EquityOrderType.QUANTITY);
        }
    }

    public toggleStopLoss() {
        this.store.toggleStopLoss();
    }

    public stopLossLimitChanged(limit: number) {
        this.store.changeStopLossLimit(limit);
    }

    private updateFormValidity(order: EquityWizardData,
                               form: IFormController,
                               quantityOwned: number,
                               hasAccount: boolean) {
        if (form["quantity"] == null) {
            return;
        }
        form.$setValidity("has-account-selected", hasAccount, form);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
